import { AllTranscriptsProps } from '@/types';
import { HIDDEN_METADATA_KEYS, isRenderableMetadataValue } from '@/utils/metadataDisplay';

/**
 * The transcripts table renders one column per conversation.metadata key.
 * Two layers keep internal bookkeeping out of it:
 *  - the explicit HIDDEN_METADATA_KEYS set (string-valued-but-internal keys
 *    like `computed_quality`);
 *  - a STRUCTURAL rule (TELV1-506): any key seen with an OBJECT value in any
 *    conversation is dropped, so the translation pipeline's per-language
 *    bookkeeping (and any future object-valued key) is hidden automatically
 *    without ever having to extend the explicit set again.
 */
const getUniqueMetadataKeys = (allConversations: AllTranscriptsProps[]): Set<string> => {
  const metadataKeys = new Set<string>();
  const objectValuedKeys = new Set<string>();

  allConversations.forEach((item) => {
    if (!item.metadata) return;
    Object.entries(item.metadata).forEach(([key, value]) => {
      if (!isRenderableMetadataValue(value)) {
        objectValuedKeys.add(key);
        return;
      }
      if (!HIDDEN_METADATA_KEYS.has(key)) {
        metadataKeys.add(key);
      }
    });
  });

  // A key that is object-valued in ANY conversation is internal bookkeeping —
  // drop it everywhere, even if another conversation happened to carry a
  // primitive under the same key.
  objectValuedKeys.forEach((key) => metadataKeys.delete(key));

  return metadataKeys;
};

export default getUniqueMetadataKeys;
